import type { ConnectError } from '@connectrpc/connect'
import {
  BgMasterErrorReason,
  BgMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `BgMasterErrorReason` (docs/adr/structured-app-error-typed-reason.md
 * decision 1/2, backend `occtomaster` domain) → i18n key + field routing
 * registry for the BgMaster Create/Update modals (design note's
 * representative path, decision E). `Record<BgMasterErrorReason,
 * AppErrorInfoReasonEntry | undefined>` forces tsc to check every generated
 * enum member is covered (Goal (2)): `UNSPECIFIED` has no dedicated message
 * (falls through to the `BG_MASTER_ERROR_KEYS` code-based fallback,
 * decision D non-breaking contract), and a new reason value added to the
 * proto enum fails to compile here until wired. `field` is the frontend's
 * own camelCase React Hook Form field name (docs/adr/
 * structured-app-error-standard-errorinfo-envelope.md decision 2: the
 * envelope never carries field, so this registry is the sole SoT for
 * reason → (field | operation) routing).
 */
const BG_MASTER_REASON_KEYS: Record<
  BgMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [BgMasterErrorReason.UNSPECIFIED]: undefined,
  [BgMasterErrorReason.BG_CODE_INVALID_FORMAT]: {
    key: 'page:adminOcctoMaster.serverError.reason.bgCodeInvalidFormat',
    field: 'bgCode',
  },
  [BgMasterErrorReason.WHEELING_LINK_CODE_INVALID_FORMAT]: {
    key: 'page:adminOcctoMaster.serverError.reason.wheelingLinkCodeInvalidFormat',
    field: 'wheelingLinkCode',
  },
  [BgMasterErrorReason.BG_CODE_ALREADY_EXISTS_CROSS_MASTER]: {
    key: 'page:adminOcctoMaster.serverError.reason.bgCodeAlreadyExistsCrossMaster',
  },
  [BgMasterErrorReason.BG_MEMBER_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.serverError.reason.bgMemberLicenseMismatch',
  },
}

/**
 * `resolveDetailedError` for BgMaster's `useCrudModalForm` (Create /
 * Update). Resolves the `google.rpc.ErrorInfo` Connect error detail, if
 * any, against `BG_MASTER_REASON_KEYS` via the `BgMasterErrorReasonSchema`
 * reflection lookup.
 */
export function resolveBgMasterDetailedError(
  err: ConnectError,
  t: TFunction,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    BgMasterErrorReasonSchema,
    BG_MASTER_REASON_KEYS,
    t,
  )
}
